import React, { useMemo } from 'react';
import MediaQuery from 'react-responsive';
import PropTypes from 'prop-types';
import uniqueId from 'lodash/uniqueId';

import LanguageSelect from 'components/LanguageSelectGroup/components/LanguageSelect';
import BasicLabel from 'components/BasicLabel';

import './styles.css';

const LanguageSelectGroup = props => {
  const {
    name,
    required,
    isValid = true,
    withoutLabel = false,
    values,
    levelOptions,
    languageOptions,
    onChange,
    onBlur,
  } = props;

  const handleLanguageSelectChange = (index, selectedOption) => {
    const updatedValues = values && values.length > 0 ? [...values] : [];

    if (updatedValues[index]) {
      updatedValues[index] = selectedOption;
    } else {
      updatedValues.push(selectedOption);
    }

    onChange(name, updatedValues);
  };

  const handleRemove = index => {
    const updatedValues =
      values.length > 0 ? [...values.filter((value, i) => i !== index)] : [];

    onChange(name, updatedValues);
  };

  const handleBlur = () => {
    if (typeof onBlur === 'function') {
      onBlur(name, true, true);
    }
  };

  const renderedRows = useMemo(() => {
    const hiddenLanguages = values
      ? values
          .filter(value => value.language)
          .map(value => value.language.value)
      : [];

    const showNewRow = !values.some(value => !value.language || !value.level);

    const rows = values
      ? values.map((value, index) => {
          return (
            <div className="language-select-group__row" key={uniqueId()}>
              <LanguageSelect
                removable={true}
                name={`item_${index}`}
                index={index}
                isValid={isValid}
                values={value}
                levelOptions={levelOptions}
                languageOptions={languageOptions}
                hiddenLanguages={hiddenLanguages.filter(language =>
                  value.language ? language !== value.language.value : true
                )}
                handleRemove={handleRemove}
                touchFunc={handleBlur}
                controlFunc={handleLanguageSelectChange}
              />
            </div>
          );
        })
      : [];

    if (showNewRow) {
      rows.push(
        <div className="language-select-group__row" key={uniqueId()}>
          <LanguageSelect
            index={rows.length}
            name={`item_${rows.length}`}
            isValid={true}
            values={{ language: null, level: null }}
            hiddenLanguages={hiddenLanguages}
            levelOptions={levelOptions}
            languageOptions={languageOptions}
            touchFunc={handleBlur}
            controlFunc={handleLanguageSelectChange}
          />
        </div>
      );
    }

    return rows;
  }, [values, isValid, languageOptions, levelOptions]);

  return (
    <div className="language-select-group">
      {!withoutLabel && (
        <MediaQuery query="(min-width: 481px)">
          <div className="language-select-group__row">
            <div className="language-select-group__element">
              <BasicLabel
                name={`${name}_languageLabel`}
                required={required}
                text="Language"
                styles={{
                  fontSize: '0.9286rem',
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: '600',
                  textTransform: 'none',
                }}
              />
            </div>
            <div className="language-select-group__element">
              <BasicLabel
                name={`${name}_languageLevel`}
                required={required}
                text="Level"
                styles={{
                  fontSize: '0.9286rem',
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: '600',
                  textTransform: 'none',
                }}
              />
            </div>
            <div className="language-select-group__spacer" />
          </div>
        </MediaQuery>
      )}
      <div className="language-select-group__container">{renderedRows}</div>
    </div>
  );
};

LanguageSelectGroup.propTypes = {
  name: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(PropTypes.object),
  required: PropTypes.bool,
  withoutLabel: PropTypes.bool,
  isValid: PropTypes.bool,
  levelOptions: PropTypes.array.isRequired,
  languageOptions: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
};

export default LanguageSelectGroup;
